// Modal that lets a signed-in user file a GitHub issue without a
// GitHub account. The server stamps the user's identity onto the
// issue body via the bearer token; this form only collects the
// human-written title + description plus a few automatically captured
// diagnostic hints (current route, browser UA, screen size).

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from 'react-oidc-context';
import { cn } from '@/lib/utils';
import { submitIssueReport, type ReportResponse } from '@/lib/useReportIssue';
import { buttonVariants } from '@/components/ui/Button';

const TITLE_MIN = 5;
const TITLE_MAX = 120;
const DESCRIPTION_MIN = 10;
const DESCRIPTION_MAX = 4000;

interface ReportIssueModalProps {
  open: boolean;
  onClose: () => void;
}

type SubmitState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'success'; data: ReportResponse }
  | { status: 'error'; message: string };

export function ReportIssueModal({ open, onClose }: ReportIssueModalProps) {
  const auth = useAuth();
  const location = useLocation();
  const titleRef = useRef<HTMLInputElement | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>({ status: 'idle' });

  // Reset form on open and focus the title field.
  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDescription('');
    setSubmitState({ status: 'idle' });
    // Defer focus so the input exists in the DOM.
    const id = window.requestAnimationFrame(() => titleRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  // Escape closes the modal unless we're mid-submit.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && submitState.status !== 'submitting') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitState.status, onClose]);

  if (!open) return null;

  const titleLen = title.trim().length;
  const descLen = description.trim().length;
  const titleOk = titleLen >= TITLE_MIN && titleLen <= TITLE_MAX;
  const descOk = descLen >= DESCRIPTION_MIN && descLen <= DESCRIPTION_MAX;
  const canSubmit =
    titleOk && descOk && submitState.status !== 'submitting' && !!auth.user?.id_token;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const token = auth.user?.id_token;
    if (!token || !titleOk || !descOk) return;

    setSubmitState({ status: 'submitting' });

    const context = {
      route: location.pathname + location.search,
      user_agent: navigator.userAgent,
      screen: `${window.innerWidth}x${window.innerHeight}`,
    };

    const result = await submitIssueReport(
      { title: title.trim(), description: description.trim(), context },
      token,
    );

    if (result.ok) {
      setSubmitState({ status: 'success', data: result.data });
    } else {
      setSubmitState({ status: 'error', message: result.message });
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-issue-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && submitState.status !== 'submitting') {
          onClose();
        }
      }}
    >
      <div className="w-full max-w-lg rounded-lg bg-card text-tres-text-primary shadow-xl">
        <div className="flex items-start justify-between border-b border-border px-5 py-4">
          <div>
            <h2 id="report-issue-title" className="text-lg font-semibold text-tres-text-primary">
              Report an Issue
            </h2>
            <p className="mt-0.5 text-xs text-tres-text-secondary">
              Filed into the TReS bug tracker. We capture your identity and the current
              page automatically — no GitHub account needed.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitState.status === 'submitting'}
            className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'ml-4')}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {submitState.status === 'success' ? (
          <div className="px-5 py-6">
            <p className="text-sm text-tres-text-primary">
              Filed as issue{' '}
              <a
                href={submitState.data.issue_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-tres-action underline"
              >
                #{submitState.data.issue_number}
              </a>
              . Thanks — we'll follow up on the ticket.
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className={buttonVariants({ variant: 'primary', size: 'lg' })}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-5 py-4">
            <label className="block">
              <span className="text-sm font-medium text-tres-text-primary">Title</span>
              <input
                ref={titleRef}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={submitState.status === 'submitting'}
                maxLength={TITLE_MAX + 20}
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action disabled:bg-muted"
                placeholder="Short summary (e.g. 'Viz expansion fails on instance nodes')"
              />
              <span
                className={cn(
                  'mt-1 block text-xs',
                  titleOk ? 'text-tres-text-secondary' : 'text-tres-error',
                )}
              >
                {titleLen}/{TITLE_MAX} {titleOk ? '' : `(min ${TITLE_MIN})`}
              </span>
            </label>

            <label className="mt-4 block">
              <span className="text-sm font-medium text-tres-text-primary">Description</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={submitState.status === 'submitting'}
                rows={6}
                maxLength={DESCRIPTION_MAX + 100}
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action disabled:bg-muted"
                placeholder="What did you do, what did you expect, what happened instead? Include any error messages."
              />
              <span
                className={cn(
                  'mt-1 block text-xs',
                  descOk ? 'text-tres-text-secondary' : 'text-tres-error',
                )}
              >
                {descLen}/{DESCRIPTION_MAX} {descOk ? '' : `(min ${DESCRIPTION_MIN})`}
              </span>
            </label>

            {submitState.status === 'error' && (
              <p className="mt-3 rounded-md bg-tres-error/5 px-3 py-2 text-xs text-tres-error">
                {submitState.message}
              </p>
            )}

            <div className="mt-5 flex items-center justify-between">
              <span className="text-xs text-tres-text-secondary">
                Page: <code className="font-mono">{location.pathname}</code>
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitState.status === 'submitting'}
                  className={buttonVariants({ variant: 'secondary', size: 'lg' })}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={buttonVariants({ variant: 'primary', size: 'lg' })}
                >
                  {submitState.status === 'submitting' ? 'Filing…' : 'File Issue'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
